/**
 * <ai-variant-selector> — 纯业务变体选择组件
 *
 * 职责：
 * - 渲染选项组 UI（颜色色板/图片色板/文本按钮）
 * - 管理选中状态、匹配变体
 * - 派发 variant:change 事件
 * - 不负责 Cart API、不渲染 drawer/modal 外壳
 *
 * 用法（内联 — product-spotlight 等）：
 * <ai-variant-selector
 *   variants='{{ product.variants | json }}'
 *   options='{{ product.options_with_values | json }}'
 *   selected-variant-id="{{ product.selected_or_first_available_variant.id }}"
 * ></ai-variant-selector>
 *
 * DOM 结构（渲染就地，样式由 LLM 在 section CSS 中定义）：
 *
 * <ai-variant-selector>
 *   .vs-product-price               ← 价格（选中变体后自动更新）
 *   .vs-option-group[data-option-name]
 *     .vs-option-label              ← 选项名
 *     .vs-option-values             ← 选项值容器
 *       button.vs-option-btn[data-value-id][data-swatch-type]
 *         data-swatch-type="image"  → 内含 img.vs-swatch-img
 *         data-swatch-type="color"  → 内含 span.vs-swatch-color（带 background-color）
 *         data-swatch-type="text"   → 纯文本
 *       .vs-option-btn--active      ← 选中态
 *   .vs-variant-status              ← 可选：售罄提示
 *
 * 事件：
 * - variant:change  { variantId, variant, available, price }
 *
 * 属性变化：
 * - 选中变体后自动更新 selected-variant-id attribute
 * - 暴露 data-available / data-sold-out 属性
 */

if (!customElements.get("ai-variant-selector")) {
    customElements.define(
        "ai-variant-selector",
        class VariantSelector extends HTMLElement {
            _variants = [];
            _options = [];
            _selectedOptions = {}; // { optionIndex: { id, name } }
            _selectedVariantId = null;
            _rendered = false;

            // ══════════════════════════════════════════
            //  生命周期
            // ══════════════════════════════════════════

            connectedCallback() {
                // 优先从属性读，fallback 到父容器 <script> 标签
                this._variants = this._parseJSON(this.getAttribute("variants")) || this._getProductData("variants") || [];
                this._options = this._parseJSON(this.getAttribute("options")) || this._getProductData("options") || [];
                this._selectedVariantId =
                    this.getAttribute("selected-variant-id") || null;

                if (!this._rendered) {
                    this._render();
                    this._rendered = true;
                }

                this._preselectDefaultVariant();
            }

            static get observedAttributes() {
                return ["variants", "options", "selected-variant-id"];
            }

            attributeChangedCallback(name, oldVal, newVal) {
                if (oldVal === newVal || !this._rendered) return;

                if (name === "variants") {
                    this._variants = this._parseJSON(newVal) || [];
                    this._rerender();
                } else if (name === "options") {
                    this._options = this._parseJSON(newVal) || [];
                    this._rerender();
                } else if (name === "selected-variant-id") {
                    this._selectedVariantId = newVal;
                    this._preselectDefaultVariant();
                }
            }

            // ══════════════════════════════════════════
            //  渲染
            // ══════════════════════════════════════════

            _render() {
                // 价格
                const priceEl = document.createElement("div");
                priceEl.className = "vs-product-price";
                const defaultVariant = this._getDefaultVariant();
                if (defaultVariant?.price) {
                    priceEl.textContent = defaultVariant.price;
                }
                this.appendChild(priceEl);

                // 选项组
                this._options.forEach((option, optIdx) => {
                    this.appendChild(this._createOptionGroup(option, optIdx));
                });
            }

            _rerender() {
                this.innerHTML = "";
                this._selectedOptions = {};
                this._rendered = false;
                this._render();
                this._rendered = true;
                this._preselectDefaultVariant();
            }

            // ══════════════════════════════════════════
            //  UI 构建
            // ══════════════════════════════════════════

            _createOptionGroup(option, optIdx) {
                const group = document.createElement("div");
                group.className = "vs-option-group";
                group.setAttribute("data-option-name", option.name);
                if (option.id != null)
                    group.setAttribute("data-option-id", String(option.id));

                const label = document.createElement("span");
                label.className = "vs-option-label";
                label.textContent = option.name;
                group.appendChild(label);

                const valuesContainer = document.createElement("div");
                valuesContainer.className = "vs-option-values";

                option.values.forEach(val => {
                    valuesContainer.appendChild(
                        this._createOptionBtn(option, val, optIdx, valuesContainer),
                    );
                });

                group.appendChild(valuesContainer);
                return group;
            }

            _createOptionBtn(option, val, optIdx, valuesContainer) {
                const resolved = this._resolveOptionValue(val);
                const valId =
                    typeof val === "object" && val !== null && val.id != null
                        ? String(val.id)
                        : null;

                const btn = document.createElement("button");
                btn.className = "vs-option-btn";
                btn.type = "button";
                btn.setAttribute("data-swatch-type", resolved.type);
                if (option.id != null)
                    btn.setAttribute("data-option-id", String(option.id));
                if (valId) btn.setAttribute("data-value-id", valId);

                // swatch 渲染：image > color > text
                if (resolved.type === "image") {
                    const img = document.createElement("img");
                    img.className = "vs-swatch-img";
                    img.src = resolved.swatchImage;
                    img.alt = resolved.name;
                    img.loading = "lazy";
                    btn.appendChild(img);
                    btn.setAttribute("title", resolved.name);
                } else if (resolved.type === "color") {
                    const swatch = document.createElement("span");
                    swatch.className = "vs-swatch-color";
                    swatch.style.backgroundColor = resolved.swatchColor;
                    btn.appendChild(swatch);
                    btn.setAttribute("title", resolved.name);
                } else {
                    btn.textContent = resolved.name;
                }

                btn.addEventListener("click", () => {
                    valuesContainer
                        .querySelectorAll(".vs-option-btn")
                        .forEach(b => b.classList.remove("vs-option-btn--active"));
                    btn.classList.add("vs-option-btn--active");
                    this._selectedOptions[optIdx] = { id: valId, name: resolved.name };
                    this._onOptionSelected();
                });

                return btn;
            }

            // ══════════════════════════════════════════
            //  选项逻辑：预选 / 选中 / 匹配
            // ══════════════════════════════════════════

            _preselectDefaultVariant() {
                const defaultVariant = this._getDefaultVariant();
                if (!defaultVariant || !defaultVariant.options) return;

                const variantValueIds = defaultVariant.options.map(
                    opt => opt.values.id,
                );

                this._options.forEach((opt, idx) => {
                    const targetId = variantValueIds[idx];
                    if (targetId == null) return;

                    const btn = this.querySelector(
                        `.vs-option-btn[data-option-id="${opt.id}"][data-value-id="${targetId}"]`,
                    );
                    if (btn) {
                        btn.classList.add("vs-option-btn--active");
                        this._selectedOptions[idx] = {
                            id: String(targetId),
                            name: btn.getAttribute("title") || btn.textContent,
                        };
                    }
                });

                this._onOptionSelected();
            }

            _onOptionSelected() {
                const selectedEntries = Object.values(this._selectedOptions);

                // 未选完 → 通知但不匹配
                if (selectedEntries.length !== this._options.length) {
                    return;
                }

                // 用 value ID 匹配 variant
                const selectedIds = this._options.map(
                    (_, idx) => this._selectedOptions[idx]?.id,
                );
                const matched = this._variants.find(v => {
                    if (!v.options) return false;
                    return v.options.every(
                        (opt, idx) => String(opt.values.id) === String(selectedIds[idx]),
                    );
                });

                if (!matched) return;

                this._selectedVariantId = matched.id;
                this.setAttribute("selected-variant-id", String(matched.id));

                // 更新价格
                const priceEl = this.querySelector(".vs-product-price");
                if (priceEl && matched.price) {
                    priceEl.textContent = matched.price;
                }

                // 更新可用性属性
                if (matched.available) {
                    this.setAttribute("data-available", "");
                    this.removeAttribute("data-sold-out");
                } else {
                    this.removeAttribute("data-available");
                    this.setAttribute("data-sold-out", "");
                }

                this.dispatchEvent(
                    new CustomEvent("variant:change", {
                        bubbles: true,
                        detail: {
                            variantId: matched.id,
                            variant: matched,
                            available: !!matched.available,
                            price: matched.price,
                        },
                    }),
                );
            }

            // ══════════════════════════════════════════
            //  数据解析工具
            // ══════════════════════════════════════════

            _getDefaultVariant() {
                return (
                    this._variants.find(
                        v => String(v.id) === String(this._selectedVariantId),
                    ) || this._variants[0]
                );
            }

            /**
             * 解析 option value 的 swatch 数据
             * 优先级：swatch.image > swatch.color > name 文本
             */
            _resolveOptionValue(val) {
                if (typeof val !== "object" || val === null) {
                    return { name: String(val), type: "text" };
                }

                const name = val.name || val.value || val.label || val.title || "";
                const swatch = val.swatch;

                if (swatch && typeof swatch === "object") {
                    if (swatch.image) {
                        const imgSrc =
                            typeof swatch.image === "object"
                                ? swatch.image.src || swatch.image.url || ""
                                : String(swatch.image);
                        if (imgSrc) {
                            return { name, type: "image", swatchImage: imgSrc };
                        }
                    }
                    if (swatch.color) {
                        return { name, type: "color", swatchColor: swatch.color };
                    }
                }

                return { name: name || JSON.stringify(val), type: "text" };
            }

            _parseJSON(str) {
                if (!str) return null;
                try {
                    return JSON.parse(str);
                } catch {
                    return null;
                }
            }

            /**
             * 从父容器（ai-product-detail / ai-product-card）的 script 标签读取数据
             * @param {'variants'|'options'} key
             * @returns {Array|null}
             */
            _getProductData(key) {
                const container = this.closest("ai-product-detail, ai-product-card");
                if (!container) return null;
                const script = container.querySelector(`script[data-product-${key}]`);
                if (!script) return null;
                try {
                    return JSON.parse(script.textContent);
                } catch {
                    return null;
                }
            }
        },
    );
}
